import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './MultipleCatalogManager.css';
import './MultipleCatalogManager-Wizard.css';
import { PrivateCatalogService } from '../../services/privateCatalogService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPen,
    faTimes,
    faPlus,
    faList,
    faTrash,
    faEye,
    faTag,
    faTags,
    faGlobe,
    faImage,
    faCheckCircle,
    faCheck,
    faCopy,
    faArrowLeft,
    faArrowRight,
    faCheckSquare,
    faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import NavInf from '../NavInf/NavInf';
import CatalogProductCard from './CatalogProductCard';
import { ClipLoader } from 'react-spinners';
import throttle from 'lodash.throttle';
import Alert from '../Alert/Alert';
import { isEmbedded } from '../../utils/isEmbedded';
import EmbeddedLayout from '../EmbeddedLayout/EmbeddedLayout';
import { getLoginRoute } from '../../utils/navigationHelper';
import PDFDesignModal from './PDFDesignModal/PDFDesignModal';

const ITEMS_PER_LOAD = 10;
const API_BASE_URL = 'https://api.minymol.com';

const MultipleCatalogManager = () => {
    const navigate = useNavigate();
    // Estados principales
    const [catalogs, setCatalogs] = useState([]);
    const [selectedCatalog, setSelectedCatalog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'preview'

    // Estados para alertas
    const [alert, setAlert] = useState(null);

    // Estados para productos
    const [productos, setProductos] = useState([]);
    const [visibleProducts, setVisibleProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const loadIndexRef = useRef(0);
    const [hasMore, setHasMore] = useState(true);

    // Estados para modales
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingCatalog, setEditingCatalog] = useState(null);

    // Estados para formularios
    const [newCatalogForm, setNewCatalogForm] = useState({
        internalName: '',
        publicName: '',
        description: '',
        type: 'ALL',
        categoryId: null,
        subcategoryId: null,
        priceField: 'price1',
        telefono: ''
    });

    const [editCatalogForm, setEditCatalogForm] = useState({
        internalName: '',
        publicName: '',
        description: '',
        priceField: 'price1',
        telefono: ''
    });

    // Estados para categorías
    const [categoriesData, setCategoriesData] = useState({ categories: [] });
    const [loadingCategories, setLoadingCategories] = useState(false);

    // Estados para banners
    const [showBannerModal, setShowBannerModal] = useState(false);
    const [bannerFile, setBannerFile] = useState(null);
    const [bannerPreview, setBannerPreview] = useState(null);
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const [bannerCatalog, setBannerCatalog] = useState(null);

    // Estados para modal de diseño PDF
    const [showPDFDesignModal, setShowPDFDesignModal] = useState(false);
    const [pdfDesignCatalog, setPdfDesignCatalog] = useState(null);

    // Estados para banner en modal de crear (wizard de pasos)
    const [createModalStep, setCreateModalStep] = useState(1); // 1: datos básicos, 2: selección productos (si CUSTOM), 3: banner
    const [createBannerFile, setCreateBannerFile] = useState(null);
    const [createBannerPreview, setCreateBannerPreview] = useState(null);

    // Feature flag: teléfono por catálogo (plan plus)
    const [hasMultipleNumbers, setHasMultipleNumbers] = useState(false);
    // Feature flag: selección de precio por catálogo (plan plus)
    const [hasMultiplePrices, setHasMultiplePrices] = useState(false);

    // Estados para catálogos personalizados (CUSTOM)
    const [availableProducts, setAvailableProducts] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [loadingAvailableProducts, setLoadingAvailableProducts] = useState(false);
    const [productSearchQuery, setProductSearchQuery] = useState('');

    // Estado para selección múltiple de subcategorías (tipo MULTI_SUBCATEGORY en UI)
    const [selectedSubcategoryIds, setSelectedSubcategoryIds] = useState([]);

    // Usuario
    const user = JSON.parse(localStorage.getItem('usuario'));

    // Effect para manejar el scroll del body cuando se abren modales
    useEffect(() => {
        const isModalOpen = showCreateModal || showEditModal || showBannerModal;
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = '15px'; // Compensar el scrollbar
        } else {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }

        // Cleanup al desmontar o cambiar
        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [showCreateModal, showEditModal, showBannerModal]);

    // Effect para cerrar modales con la tecla ESC
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                if (showCreateModal) {
                    setShowCreateModal(false);
                } else if (showEditModal) {
                    setShowEditModal(false);
                } else if (showBannerModal) {
                    setShowBannerModal(false);
                }
            }
        };

        if (showCreateModal || showEditModal || showBannerModal) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [showCreateModal, showEditModal, showBannerModal]);

    // Configuración de breakpoints para Masonry - No necesario con grid CSS
    // const breakpointColumnsObj = {
    //     default: 6,
    //     1600: 6,
    //     1200: 5,
    //     768: 3,
    //     480: 2
    // };

    const loadMore = useCallback(() => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);

        setTimeout(() => {
            const next = productos.slice(
                loadIndexRef.current,
                loadIndexRef.current + ITEMS_PER_LOAD
            );
            setVisibleProducts(prev => [...prev, ...next]);
            loadIndexRef.current += ITEMS_PER_LOAD;
            if (loadIndexRef.current >= productos.length) {
                setHasMore(false);
            }
            setLoadingMore(false);
        }, 500);
    }, [productos, loadingMore, hasMore]);

    const handleScroll = useCallback(() => {
        if (loadingProducts || loadingMore || !hasMore) return;

        if (
            window.innerHeight +
            document.documentElement.scrollTop +
            200 >=
            document.documentElement.offsetHeight
        ) {
            loadMore();
        }
    }, [loadingProducts, loadingMore, hasMore, loadMore]);

    // Función para obtener productos según el catálogo seleccionado
    const fetchProducts = useCallback(async (catalog = null) => {
        try {
            setLoadingProducts(true);
            setVisibleProducts([]);
            loadIndexRef.current = 0;
            setHasMore(true);

            const userData = JSON.parse(localStorage.getItem('usuario'));
            if (!userData?.proveedorInfo?.id) {
                return;
            }

            let products = [];

            // Para vista previa del catálogo, obtenemos los productos usando el endpoint público
            // que simula lo que verán los comerciantes
            try {
                let catalogData;

                // Usar el catálogo pasado como parámetro o el seleccionado globalmente
                const targetCatalog = catalog || selectedCatalog;

                // Si hay un catálogo seleccionado específico, usar ese endpoint
                if (targetCatalog && targetCatalog.id) {
                    catalogData = await PrivateCatalogService.getCatalogProductsByCatalogId(targetCatalog.id);
                } else {
                    // Si no hay catálogo seleccionado, usar el endpoint general del proveedor
                    catalogData = await PrivateCatalogService.getCatalogProducts(userData.proveedorInfo.id);
                }

                if (catalogData && catalogData.products) {
                    products = catalogData.products;
                } else if (Array.isArray(catalogData)) {
                    products = catalogData;
                } else {
                    products = [];
                }
            } catch (catalogError) {
                console.warn("No se pudieron obtener productos del catálogo público, intentando método alternativo:", catalogError);

                // Fallback: obtener productos directamente del proveedor
                const res = await fetch(`https://api.minymol.com/products/by-provider/${userData.proveedorInfo.id}`);

                if (!res.ok) {
                    if (res.status === 429) {
                        setLoadingProducts(false);
                        return;
                    }
                    throw new Error(`HTTP ${res.status}`);
                }

                const data = await res.json();
                const ids = data.map(p => p.product_id).filter(Boolean);

                if (!ids.length) {
                    setProductos([]);
                    return;
                }

                const previewsRes = await fetch('https://api.minymol.com/products/previews', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ids })
                });

                if (!previewsRes.ok) {
                    if (previewsRes.status === 429) {
                        setLoadingProducts(false);
                        return;
                    }
                    throw new Error(`HTTP ${previewsRes.status}`);
                }

                const previews = await previewsRes.json();
                products = previews.filter(product => !product.isInactive);
            }

            setProductos(products);
        } catch (err) {
            if (!err.message?.includes('429')) {
                console.error("Error al obtener productos:", err);
            }
            setProductos([]);
        } finally {
            setLoadingProducts(false);
        }
    }, [selectedCatalog]);

    // Función para cargar catálogos
    const fetchCatalogs = useCallback(async (shouldFetchProducts = true) => {
        try {
            setLoading(true);
            const catalogsList = await PrivateCatalogService.getMyCatalogs();

            // Asegurar que catalogsList sea un array válido
            const validCatalogs = Array.isArray(catalogsList) ? catalogsList : [];
            setCatalogs(validCatalogs);

            // Seleccionar catálogo general por defecto o el primero
            const generalCatalog = validCatalogs.find(c => c.type === 'ALL');
            const defaultCatalog = generalCatalog || validCatalogs[0] || null;
            setSelectedCatalog(defaultCatalog);

            // Solo cargar productos si se solicita y tenemos un catálogo por defecto
            if (shouldFetchProducts && defaultCatalog) {
                // Llamar fetchProducts directamente sin depender del useCallback
                try {
                    setLoadingProducts(true);
                    setVisibleProducts([]);
                    loadIndexRef.current = 0;
                    setHasMore(true);

                    const userData = JSON.parse(localStorage.getItem('usuario'));
                    if (!userData?.proveedorInfo?.id) {
                        return;
                    }

                    let products = [];

                    try {
                        let catalogData;

                        if (defaultCatalog && defaultCatalog.id) {
                            catalogData = await PrivateCatalogService.getCatalogProductsByCatalogId(defaultCatalog.id);
                        } else {
                            catalogData = await PrivateCatalogService.getCatalogProducts(userData.proveedorInfo.id);
                        }

                        if (catalogData && catalogData.products) {
                            products = catalogData.products;
                        } else if (Array.isArray(catalogData)) {
                            products = catalogData;
                        } else {
                            products = [];
                        }
                    } catch (catalogError) {
                        console.warn("No se pudieron obtener productos del catálogo público, intentando método alternativo:", catalogError);

                        const res = await fetch(`https://api.minymol.com/products/by-provider/${userData.proveedorInfo.id}`);
                        if (!res.ok) {
                            if (res.status === 429) {
                                setLoadingProducts(false);
                                return;
                            }
                            throw new Error(`HTTP ${res.status}`);
                        }

                        const data = await res.json();
                        const ids = data.map(p => p.product_id).filter(Boolean);

                        if (!ids.length) {
                            setProductos([]);
                            return;
                        }

                        const previewsRes = await fetch('https://api.minymol.com/products/previews', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ ids })
                        });

                        if (!previewsRes.ok) {
                            if (previewsRes.status === 429) {
                                setLoadingProducts(false);
                                return;
                            }
                            throw new Error(`HTTP ${previewsRes.status}`);
                        }

                        const previews = await previewsRes.json();
                        products = previews.filter(product => !product.isInactive);
                    }

                    setProductos(products);
                } catch (err) {
                    if (!err.message?.includes('429')) {
                        console.error("Error al obtener productos:", err);
                    }
                    setProductos([]);
                } finally {
                    setLoadingProducts(false);
                }
            }
        } catch (error) {
            console.error('Error al obtener catálogos:', error);
            // Si es el error específico de JSON vacío, manejar como lista vacía
            if (error.message && error.message.includes('Unexpected end of JSON input')) {
                setCatalogs([]);
                setSelectedCatalog(null);
                setAlert({ type: 'info', message: 'No tienes catálogos creados aún. ¡Crea tu primer catálogo!' });
            } else {
                setAlert({ type: 'error', message: 'Error al cargar catálogos. Por favor, intenta de nuevo.' });
            }
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    // Función para cargar opciones disponibles y categorías
    const fetchAvailableOptions = async () => {
        try {
            setLoadingCategories(true);
            const [myCategories] = await Promise.all([
                PrivateCatalogService.getMyCategories()
            ]);

            // Solo usamos las categorías, las opciones no se necesitan actualmente
            setCategoriesData(myCategories);
        } catch (error) {
            console.error('Error al obtener opciones disponibles:', error);
            setAlert({ type: 'error', message: 'Error al cargar opciones de catálogos' });
        } finally {
            setLoadingCategories(false);
        }
    };

    // Función para cargar productos disponibles para el selector
    const fetchAvailableProducts = async () => {
        try {
            setLoadingAvailableProducts(true);
            const products = await PrivateCatalogService.getProductsForSelection();
            setAvailableProducts(products || []);
        } catch (error) {
            console.error('Error al obtener productos disponibles:', error);
            setAlert({ type: 'error', message: 'Error al cargar productos' });
            setAvailableProducts([]);
        } finally {
            setLoadingAvailableProducts(false);
        }
    };



    // Effect para manejar cambios en el catálogo seleccionado
    useEffect(() => {
        if (selectedCatalog) {
            fetchProducts(selectedCatalog);
        }
    }, [selectedCatalog, fetchProducts]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                const userData = JSON.parse(localStorage.getItem('usuario'));
                if (!userData?.proveedorInfo?.id) {
                    setAlert({ type: 'error', message: 'Solo los proveedores pueden gestionar catálogos privados.' });
                    return;
                }
                fetchCatalogs(true);
                fetchAvailableOptions();

                // Cargar features del proveedor (plan plus)
                user.getIdToken().then(token => {
                    fetch(`${API_BASE_URL}/providers/me/features`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                        .then(res => res.ok ? res.json() : null)
                        .then(features => {
                            if (features) setHasMultipleNumbers(features.hasMultipleNumbers ?? false);
                            if (features) setHasMultiplePrices(features.hasMultiplePrices ?? false);
                        })
                        .catch(() => {});
                });
            } else {
                // Si no hay usuario autenticado, redirigir a login
                console.log('❌ No hay usuario autenticado, redirigiendo a login');
                navigate(getLoginRoute());
            }
        });

        return () => unsubscribe();
    }, [fetchCatalogs, navigate]);

    useEffect(() => {
        if (!loadingProducts && productos.length > 0) {
            const inicial = productos.slice(0, ITEMS_PER_LOAD);
            setVisibleProducts(inicial);
            loadIndexRef.current = ITEMS_PER_LOAD;
            setHasMore(productos.length > ITEMS_PER_LOAD);
        }
    }, [loadingProducts, productos]);

    useEffect(() => {
        const throttledScroll = throttle(handleScroll, 200);
        window.addEventListener('scroll', throttledScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', throttledScroll);
            throttledScroll.cancel();
        };
    }, [handleScroll]);

    // Funciones auxiliares para modales
    const openCreateModal = async () => {
        setNewCatalogForm({
            internalName: '',
            publicName: '',
            description: '',
            type: 'ALL',
            categoryId: null,
            subcategoryId: null,
            priceField: 'price1',
            telefono: ''
        });
        setCreateModalStep(1);
        setCreateBannerFile(null);
        setCreateBannerPreview(null);
        setSelectedProductIds([]);
        setSelectedSubcategoryIds([]);
        setProductSearchQuery('');

        // Cargar productos disponibles para el selector
        await fetchAvailableProducts();

        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setCreateModalStep(1);
        setNewCatalogForm({
            internalName: '',
            publicName: '',
            description: '',
            type: 'ALL',
            categoryId: null,
            subcategoryId: null,
            priceField: 'price1',
            telefono: ''
        });
        setCreateBannerFile(null);
        setCreateBannerPreview(null);
        setSelectedProductIds([]);
        setSelectedSubcategoryIds([]);
        setProductSearchQuery('');
    };

    // Función para avanzar al siguiente paso
    const goToNextStep = () => {
        // Validar paso 1 antes de continuar
        if (!newCatalogForm.internalName.trim()) {
            setAlert({ type: 'error', message: 'El nombre interno del catálogo es obligatorio' });
            return;
        }

        if (!newCatalogForm.publicName.trim()) {
            setAlert({ type: 'error', message: 'El nombre público del catálogo es obligatorio' });
            return;
        }

        if (newCatalogForm.type === 'SUBCATEGORY') {
            // Si hay múltiples categorías, validar que se haya seleccionado una categoría
            if (categoriesData.categories?.length > 1 && !newCatalogForm.categoryId) {
                setAlert({ type: 'error', message: 'Debes seleccionar una categoría primero' });
                return;
            }

            // Validar que se haya seleccionado una subcategoría
            if (!newCatalogForm.subcategoryId) {
                setAlert({ type: 'error', message: 'Debes seleccionar una categoría' });
                return;
            }
        }

        if (newCatalogForm.type === 'MULTI_SUBCATEGORY') {
            if (selectedSubcategoryIds.length === 0) {
                setAlert({ type: 'error', message: 'Debes seleccionar al menos una categoría' });
                return;
            }
        }

        if (newCatalogForm.type === 'CUSTOM' && selectedProductIds.length === 0) {
            setAlert({ type: 'error', message: 'Selecciona al menos un producto' });
            return;
        }

        // Ir directo al paso del banner (paso 2 ahora, ya que eliminamos el paso intermedio)
        setCreateModalStep(2);
    };

    // Función para volver al paso anterior
    const goBackToBasicData = () => {
        setCreateModalStep(1);
    };

    // Validar tamaño de imagen (sin compresión)
    const validateImageSize = (file) => {
        const ua = navigator.userAgent;
        const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.maxTouchPoints > 1);
        const maxSizeBytes = isiOS ? 400 * 1024 : 300 * 1024;
        const maxSizeLabel = isiOS ? '400KB' : '300KB';

        if (file.size > maxSizeBytes) {
            const realKB = (file.size / 1024).toFixed(1);
            throw new Error(`La imagen pesa ${realKB}KB. Debe pesar máximo ${maxSizeLabel}.`);
        }
    };

    // Función para manejar archivo de banner en modal de crear
    const handleCreateBannerFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setAlert({ type: 'error', message: 'Por favor selecciona un archivo de imagen válido' });
            return;
        }

        try {
            validateImageSize(file);
        } catch (error) {
            setAlert({ type: 'error', message: error.message });
            return;
        }

        setCreateBannerFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setCreateBannerPreview(e.target.result);
        reader.readAsDataURL(file);
    };

    const openEditModal = (catalog) => {
        setEditingCatalog(catalog);
        setEditCatalogForm({
            internalName: catalog.internalName || catalog.name,
            publicName: catalog.publicName || catalog.name,
            description: catalog.description || '',
            priceField: catalog.priceField || 'price1',
            telefono: catalog.telefono || ''
        });
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingCatalog(null);
        setEditCatalogForm({
            internalName: '',
            publicName: '',
            description: '',
            priceField: 'price1',
            telefono: ''
        });
    };

    // Funciones auxiliares para banner
    const openBannerModal = (catalog) => {
        setBannerCatalog(catalog);
        setBannerFile(null);
        setBannerPreview(null);
        setShowBannerModal(true);
    };

    const closeBannerModal = () => {
        setShowBannerModal(false);
        setBannerCatalog(null);
        setBannerFile(null);
        setBannerPreview(null);
    };

    // Funciones para modal de diseño PDF
    const openPDFDesignModal = (catalog) => {
        setPdfDesignCatalog(catalog);
        setShowPDFDesignModal(true);
    };

    const closePDFDesignModal = () => {
        setShowPDFDesignModal(false);
        setPdfDesignCatalog(null);
    };

    const handleBannerFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setAlert({ type: 'error', message: 'Por favor selecciona un archivo de imagen válido' });
            return;
        }

        try {
            validateImageSize(file);
        } catch (error) {
            setAlert({ type: 'error', message: error.message });
            return;
        }

        setBannerFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setBannerPreview(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleBannerUpload = async () => {
        if (!bannerFile || !bannerCatalog) return;

        try {
            setUploadingBanner(true);

            // Usar el nuevo flujo específico por catalogId
            await PrivateCatalogService.uploadBannerById(bannerCatalog.id, bannerFile);

            // Actualizar catálogos
            await fetchCatalogs(false);
            closeBannerModal();
            setAlert({ type: 'success', message: 'Banner actualizado exitosamente' });

        } catch (error) {
            console.error('Error al subir banner:', error);
            setAlert({ type: 'error', message: error.message || 'Error al subir banner' });
        } finally {
            setUploadingBanner(false);
        }
    };

    // Función para generar enlace compartible del catálogo
    const generateCatalogShareLink = async (catalogId, catalogName, catalogBannerUrl = null) => {
        try {
            const user = JSON.parse(localStorage.getItem('usuario'));
            const providerId = user?.proveedorInfo?.id;

            if (!providerId || !catalogId) {
                throw new Error('Faltan datos del proveedor o catálogo');
            }

            // Construir la URL de redirección con los parámetros del catálogo privado
            const redirectUrl = `https://ovicatalogos.com/?catalog-private=${catalogId}&provider-id=${providerId}`;

            // Usar la imagen del catálogo (banner subido) o una imagen por defecto
            const imageUrl = catalogBannerUrl || 'https://cdn.minymol.com/catalog-banners/banner_defecto.jpg';

            const shareData = {
                title: catalogName || 'Catálogo Privado',
                imageUrl: imageUrl,
                redirectUrl: redirectUrl
            };

            const response = await fetch(`${API_BASE_URL}/share`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(shareData),
            });

            const result = await response.json();

            if (!response.ok || !result?.shareUrl) {
                throw new Error(result?.message || 'Error al crear enlace');
            }

            return result.shareUrl;
        } catch (error) {
            console.error('Error al generar enlace del catálogo:', error);
            throw error;
        }
    };

    // Funciones para manejar formularios
    const handleNewCatalogFormChange = (field, value) => {
        setNewCatalogForm(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpiar subcategoría cuando se cambia la categoría
        if (field === 'categoryId') {
            setNewCatalogForm(prev => ({
                ...prev,
                subcategoryId: null
            }));
        }
    };

    // Funciones para manejar selección de productos
    const toggleProductSelection = (productId) => {
        setSelectedProductIds(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
    };

    const isProductSelected = (productId) => {
        return selectedProductIds.includes(productId);
    };

    const filteredAvailableProducts = availableProducts.filter(product =>
        product.name.toLowerCase().includes(productSearchQuery.toLowerCase())
    );

    const handleCreateCatalog = async () => {
        // Validar que se haya subido el banner
        if (!createBannerFile) {
            setAlert({ type: 'error', message: 'Debes seleccionar un banner para el catálogo' });
            return;
        }

        try {
            setSaving(true);

            // Transformar los datos según la nueva lógica
            let catalogData = { ...newCatalogForm };

            // Agregar productIds si es tipo CUSTOM
            if (catalogData.type === 'CUSTOM') {
                catalogData.productIds = selectedProductIds;
            }

            // MULTI_SUBCATEGORY: tipo visual → enviar SUBCATEGORY al backend con subcategoryIds
            if (catalogData.type === 'MULTI_SUBCATEGORY') {
                catalogData = {
                    ...catalogData,
                    type: 'SUBCATEGORY',
                    subcategoryIds: selectedSubcategoryIds,
                    subcategoryId: undefined,
                };
            }

            // Si solo hay una categoría y el tipo es 'ALL', convertir a 'CATEGORY'
            if (catalogData.type === 'ALL' && categoriesData.categories.length === 1) {
                catalogData = {
                    ...catalogData,
                    type: 'CATEGORY',
                    categoryId: categoriesData.categories[0].id
                };
            }

            const newCatalog = await PrivateCatalogService.createCatalog(catalogData);

            // Subir el banner (ahora es obligatorio)
            let catalogBannerUrl = null;
            try {
                await PrivateCatalogService.uploadBannerById(newCatalog.id, createBannerFile);

                // Después de subir el banner, obtener la URL actualizada del catálogo
                // Refrescar datos del catálogo para obtener la URL del banner
                await fetchCatalogs(false);
                const updatedCatalogs = await PrivateCatalogService.getMyCatalogs();
                const updatedCatalog = updatedCatalogs.find(c => c.id === newCatalog.id);
                catalogBannerUrl = updatedCatalog?.bannerUrl;

            } catch (bannerError) {
                console.error('Error al subir banner:', bannerError);
                setAlert({ type: 'error', message: 'Error al subir el banner. Inténtalo de nuevo.' });
                setSaving(false);
                return;
            }

            // Generar enlace compartible del catálogo
            try {
                const shareLink = await generateCatalogShareLink(newCatalog.id, newCatalog.publicName || newCatalog.name, catalogBannerUrl);

                // Actualizar el catálogo con el enlace generado
                await PrivateCatalogService.updateCatalog(newCatalog.id, { enlace: shareLink });

                console.log('✅ Enlace del catálogo generado y actualizado:', shareLink);
            } catch (linkError) {
                console.error('Error al generar enlace del catálogo:', linkError);
                // No fallar todo el proceso por el enlace, solo mostrar warning
                setAlert({ type: 'info', message: 'Catálogo creado exitosamente, pero no se pudo generar el enlace compartible' });
            }

            // Actualizar lista de catálogos
            await fetchCatalogs(false);

            // Seleccionar el nuevo catálogo
            setSelectedCatalog(newCatalog);

            // Limpiar formulario y cerrar modal
            closeCreateModal();

            setAlert({ type: 'success', message: 'Catálogo y banner creados exitosamente' });
        } catch (error) {
            console.error('Error al crear catálogo:', error);
            setAlert({ type: 'error', message: error.message || 'Error al crear catálogo' });
        } finally {
            setSaving(false);
        }
    };

    const handleEditCatalog = async () => {
        if (!editCatalogForm.internalName.trim()) {
            setAlert({ type: 'error', message: 'El nombre interno del catálogo es obligatorio' });
            return;
        }

        if (!editCatalogForm.publicName.trim()) {
            setAlert({ type: 'error', message: 'El nombre público del catálogo es obligatorio' });
            return;
        }

        try {
            setSaving(true);
            await PrivateCatalogService.updateCatalog(editingCatalog.id, editCatalogForm);

            // Actualizar lista de catálogos
            await fetchCatalogs(false);

            closeEditModal();
            setAlert({ type: 'success', message: 'Catálogo actualizado exitosamente' });
        } catch (error) {
            console.error('Error al actualizar catálogo:', error);
            setAlert({ type: 'error', message: error.message || 'Error al actualizar catálogo' });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCatalog = async (catalogId) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar este catálogo?')) {
            return;
        }

        try {
            setSaving(true);
            await PrivateCatalogService.deleteCatalog(catalogId);

            // Actualizar lista de catálogos
            await fetchCatalogs(false);

            setAlert({ type: 'success', message: 'Catálogo eliminado exitosamente' });
        } catch (error) {
            console.error('Error al eliminar catálogo:', error);
            setAlert({ type: 'error', message: error.message || 'Error al eliminar catálogo' });
        } finally {
            setSaving(false);
        }
    };

    // Función para obtener icono según tipo de catálogo
    const getCatalogIcon = (type) => {
        switch (type) {
            case 'ALL':
                return faGlobe;
            case 'CATEGORY':
                return faTag;
            case 'SUBCATEGORY':
                return faTags;
            case 'CUSTOM':
                return faCheckSquare;
            default:
                return faList;
        }
    };

    // Función para obtener descripción del tipo
    const getCatalogTypeDescription = (catalog) => {
        switch (catalog.type) {
            case 'all':
                return 'Catálogo General';
            case 'category':
                return `Catálogo General`;
            case 'subcategory':
                // Si tiene subcategorías múltiples (nuevo flujo) o una sola (legacy)
                if (catalog.catalogSubcategories?.length > 1) {
                    return `Varias Categorías (${catalog.catalogSubcategories.length})`;
                }
                return `Categoría: ${catalog.subcategory?.name || catalog.catalogSubcategories?.[0]?.subcategory?.name || 'N/A'}`;
            case 'custom':
                return 'Catálogo Personalizado';
            default:
                return catalog.type;
        }
    };

    if (loading) {
        return (
            <>
                {!isEmbedded() && <Header />}
                {isEmbedded() && <EmbeddedLayout />}
                <div className="multiple-catalog-manager">
                    <div className="multiple-catalog-manager-loading">
                        <ClipLoader color="#fa7e17" size={30} />
                        <p>Cargando catálogos...</p>
                    </div>
                </div>
                {!isEmbedded() && <Footer />}
            </>
        );
    }

    return (
        <>
            {!isEmbedded() && <Header />}

            {/* Layout embebido (sidebar + header) cuando está en iframe */}
            {isEmbedded() && <EmbeddedLayout />}

            <div className="multiple-catalog-manager">
                <div className="multiple-catalog-content">

                    {/* Header con navegación */}
                    <div className="multiple-catalog-header">
                        <div className="multiple-catalog-nav">
                            <button
                                className={`multiple-catalog-nav-btn multiple-catalog-nav-btn-active`}
                                onClick={() => setViewMode('list')}
                            >
                                <FontAwesomeIcon icon={faList} />
                                Mis Catálogos
                            </button>
                        </div>

                        <button
                            className="multiple-catalog-create-btn"
                            onClick={openCreateModal}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            Crear Catálogo
                        </button>
                    </div>

                    {/* Vista de lista de catálogos */}
                    {viewMode === 'list' && (
                        <div className="multiple-catalog-list-view">
                            <h2>Gestionar Catálogos</h2>
                            <p className="multiple-catalog-description">
                                Crea y gestiona múltiples catálogos para organizar tus productos por categorías o para diferentes audiencias.
                            </p>

                            {catalogs.length === 0 ? (
                                <div className="multiple-catalog-no-catalogs">
                                    <FontAwesomeIcon icon={faList} />
                                    <h3>No tienes catálogos</h3>
                                    <p>Crea tu primer catálogo para comenzar a organizar tus productos</p>
                                    <button
                                        className="multiple-catalog-create-first-btn"
                                        onClick={openCreateModal}
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        Crear mi primer catálogo
                                    </button>
                                </div>
                            ) : (
                                <div className="multiple-catalog-grid">
                                    {catalogs.map(catalog => (
                                        <div key={catalog.id} className="multiple-catalog-card">
                                            <div className="multiple-catalog-card-header">
                                                <div className="multiple-catalog-info">
                                                    <div className="multiple-catalog-icon">
                                                        <FontAwesomeIcon icon={getCatalogIcon(catalog.type)} />
                                                    </div>
                                                    <div className="multiple-catalog-details">
                                                        <h3>{catalog.publicName || catalog.name}</h3>
                                                        <p className="multiple-catalog-type">{getCatalogTypeDescription(catalog)}</p>
                                                        {catalog.internalName && catalog.internalName !== catalog.publicName && (
                                                            <p className="multiple-catalog-internal-name" style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                                                                ID: {catalog.internalName}
                                                            </p>
                                                        )}
                                                        {catalog.description && (
                                                            <p className="multiple-catalog-card-description">{catalog.description}</p>
                                                        )}

                                                        {/* Enlace compartible del catálogo */}
                                                        {catalog.enlace && (
                                                            <div className="multiple-catalog-url-container">
                                                                <label className="multiple-catalog-url-label">
                                                                    Enlace
                                                                </label>
                                                                <div className="multiple-catalog-url-copy">
                                                                    <input
                                                                        type="text"
                                                                        value={catalog.enlace}
                                                                        readOnly
                                                                        className="multiple-catalog-url-input"
                                                                        onClick={(e) => e.target.select()}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="multiple-catalog-url-copy-btn"
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(catalog.enlace).then(() => {
                                                                                setAlert({ type: 'success', message: '¡Enlace copiado al portapapeles!' });
                                                                            }).catch(() => {
                                                                                setAlert({ type: 'error', message: 'No se pudo copiar el enlace' });
                                                                            });
                                                                        }}
                                                                        title="Copiar enlace"
                                                                    >
                                                                        <FontAwesomeIcon icon={faCopy} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="multiple-catalog-url-whatsapp-btn"
                                                                        onClick={() => {
                                                                            const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(catalog.enlace)}`;
                                                                            window.open(whatsappUrl, '_blank');
                                                                        }}
                                                                        title="Compartir por WhatsApp"
                                                                    >
                                                                        <FontAwesomeIcon icon={faWhatsapp} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="multiple-catalog-card-actions">
                                                <button
                                                    className="multiple-catalog-action-btn multiple-catalog-view-btn"
                                                    onClick={() => {
                                                        if (catalog.enlace) {
                                                            window.open(catalog.enlace, '_blank');
                                                        } else {
                                                            setAlert({ type: 'error', message: 'Este catálogo no tiene enlace disponible' });
                                                        }
                                                    }}
                                                    title="Abrir catálogo"
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                </button>
                                                <button
                                                    className="multiple-catalog-action-btn multiple-catalog-pdf-btn"
                                                    onClick={() => openPDFDesignModal(catalog)}
                                                    title="Generar PDF"
                                                >
                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                </button>
                                                <button
                                                    className="multiple-catalog-action-btn multiple-catalog-banner-btn"
                                                    onClick={() => openBannerModal(catalog)}
                                                    title="Cambiar banner"
                                                >
                                                    <FontAwesomeIcon icon={faImage} />
                                                </button>
                                                <button
                                                    className="multiple-catalog-action-btn multiple-catalog-edit-btn"
                                                    onClick={() => openEditModal(catalog)}
                                                    title="Editar catálogo"
                                                >
                                                    <FontAwesomeIcon icon={faPen} />
                                                </button>
                                                {catalog.type !== 'ALL' && (
                                                    <button
                                                        className="multiple-catalog-action-btn multiple-catalog-delete-btn"
                                                        onClick={() => handleDeleteCatalog(catalog.id)}
                                                        title="Eliminar catálogo"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Vista de preview del catálogo seleccionado */}
                    {viewMode === 'preview' && selectedCatalog && (
                        <div className="multiple-catalog-preview-view">
                            <div className="multiple-catalog-preview-header">
                                <div className="multiple-catalog-preview-banner-container">
                                    <img
                                        src={selectedCatalog.bannerUrl || user?.proveedorInfo?.banner_url || 'https://cdn.minymol.com/catalog-banners/banner_defecto.jpg'}
                                        alt="Banner del catálogo"
                                        className="multiple-catalog-preview-banner-image"
                                        onError={(e) => {
                                            e.target.src = 'https://cdn.minymol.com/catalog-banners/banner_defecto.jpg';
                                        }}
                                    />
                                    <div className="multiple-catalog-preview-banner-overlay">
                                        <div className="multiple-catalog-preview-banner-content">
                                            <h1>{selectedCatalog.publicName || selectedCatalog.name}</h1>
                                            <p>{getCatalogTypeDescription(selectedCatalog)} - {visibleProducts.length} productos</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Productos del catálogo */}
                            {loadingProducts ? (
                                <div className="multiple-catalog-preview-products-loading">
                                    <ClipLoader color="#fa7e17" size={25} />
                                    <p>Cargando productos...</p>
                                </div>
                            ) : (
                                <>
                                    {visibleProducts.length > 0 ? (
                                        <div className="multiple-catalog-products-grid">
                                            {visibleProducts.map((prod, index) => (
                                                <CatalogProductCard key={`catalog-card-${prod.id || index}`} product={prod} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="multiple-catalog-preview-no-products">
                                            <p>No hay productos en este catálogo</p>
                                        </div>
                                    )}

                                    {loadingMore && hasMore && (
                                        <div className="multiple-catalog-preview-loader-bottom">
                                            <ClipLoader color="#fa7e17" size={25} />
                                        </div>
                                    )}

                                    {!hasMore && visibleProducts.length > 0 && (
                                        <div className="multiple-catalog-preview-end-message">
                                            No hay más productos para mostrar
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                </div>

                {/* Modal para crear catálogo - Wizard de pasos */}
                {showCreateModal && (
                    <div className="multiple-catalog-modal-overlay" onClick={closeCreateModal}>
                        <div className="multiple-catalog-modal-content catalog-wizard-modal" onClick={e => e.stopPropagation()}>

                            {/* Header con indicador de pasos */}
                            <div className="catalog-wizard-header">
                                <h3 className="catalog-wizard-title">
                                    Crear Nuevo Catálogo
                                </h3>
                                <div className="catalog-wizard-steps-container">
                                    <div className={`catalog-wizard-step-indicator ${createModalStep === 1 ? 'wizard-step-active' : createModalStep > 1 ? 'wizard-step-completed' : ''}`}>
                                        <div className="catalog-wizard-step-number">1</div>
                                        <div className="catalog-wizard-step-label">
                                            {newCatalogForm.type === 'CUSTOM' ? 'Configuración y Productos' : 'Información Básica'}
                                        </div>
                                    </div>
                                    <div className="catalog-wizard-step-divider"></div>
                                    <div className={`catalog-wizard-step-indicator ${createModalStep === 2 ? 'wizard-step-active' : ''}`}>
                                        <div className="catalog-wizard-step-number">2</div>
                                        <div className="catalog-wizard-step-label">Banner del Catálogo</div>
                                    </div>
                                </div>
                            </div>

                            {/* Contenido del paso actual */}
                            <div className="catalog-wizard-body">

                                {/* Paso 1: Información básica */}
                                <div className={`catalog-wizard-step-content ${createModalStep === 1 ? 'wizard-step-content-active' : ''}`}>
                                    <div className="catalog-wizard-form-group">
                                        <label>Nombre Interno (Identificación) *</label>
                                        <input
                                            type="text"
                                            value={newCatalogForm.internalName}
                                            onChange={e => handleNewCatalogFormChange('internalName', e.target.value)}
                                            placeholder="Ej: CAT-VERANO-2024"
                                            maxLength={100}
                                        />
                                        <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                                            Nombre único para tu gestión interna (no visible para comerciantes)
                                        </small>
                                    </div>

                                    <div className="catalog-wizard-form-group">
                                        <label>Nombre Público (Visible) *</label>
                                        <input
                                            type="text"
                                            value={newCatalogForm.publicName}
                                            onChange={e => handleNewCatalogFormChange('publicName', e.target.value)}
                                            placeholder="Ej: Colección Verano"
                                            maxLength={100}
                                        />
                                        <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                                            Nombre que verán los comerciantes (puede repetirse)
                                        </small>
                                    </div>

                                    <div className="catalog-wizard-form-group">
                                        <label>Descripción</label>
                                        <input
                                            type="text"
                                            value={newCatalogForm.description}
                                            onChange={e => handleNewCatalogFormChange('description', e.target.value)}
                                            placeholder="Describe tu catálogo en pocas palabras..."
                                            maxLength={150}
                                        />
                                    </div>

                                    {hasMultipleNumbers && (
                                    <div className="catalog-wizard-form-group">
                                        <label>Teléfono de WhatsApp (Opcional)</label>
                                        <input
                                            type="tel"
                                            value={newCatalogForm.telefono}
                                            onChange={e => handleNewCatalogFormChange('telefono', e.target.value)}
                                            placeholder="Ej: +573001234567"
                                            maxLength={20}
                                        />
                                        <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                                            Si no se especifica, se usará el teléfono del proveedor
                                        </small>
                                    </div>
                                    )}

                                    {hasMultiplePrices && (
                                    <div className="catalog-wizard-form-group">
                                        <label>Precio a Mostrar (Opcional)</label>
                                        <select
                                            value={newCatalogForm.priceField || ''}
                                            onChange={e => handleNewCatalogFormChange('priceField', e.target.value || null)}
                                        >
                                            <option value="">Todos los precios (por defecto)</option>
                                            <option value="price1">Precio 1</option>
                                            <option value="price2">Precio 2</option>
                                            <option value="price3">Precio 3</option>
                                        </select>
                                        <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                                            Útil si tienes diferentes precios para diferentes vendedores o ciudades
                                        </small>
                                    </div>
                                    )}

                                    <div className="catalog-wizard-form-group">
                                        <label>Tipo de Catálogo *</label>
                                        <select
                                            value={newCatalogForm.type}
                                            onChange={e => {
                                                handleNewCatalogFormChange('type', e.target.value);
                                                setSelectedSubcategoryIds([]);
                                            }}
                                        >
                                            <option value="ALL">Catálogo General</option>
                                            <option value="SUBCATEGORY">Por Categoría</option>
                                            <option value="MULTI_SUBCATEGORY">Varias Categorías</option>
                                            <option value="CUSTOM">Catálogo Personalizado</option>
                                        </select>
                                        {newCatalogForm.type === 'CUSTOM' && (
                                            <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                                                Selecciona manualmente qué productos incluir en este catálogo
                                            </small>
                                        )}
                                        {newCatalogForm.type === 'MULTI_SUBCATEGORY' && (
                                            <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                                                Selecciona varias categorías para mostrar sus productos juntos
                                            </small>
                                        )}
                                    </div>

                                    {newCatalogForm.type === 'SUBCATEGORY' && (
                                        <>
                                            {/* Si hay múltiples categorías, mostrar selector de categoría */}
                                            {categoriesData.categories?.length > 1 && (
                                                <div className="catalog-wizard-form-group">
                                                    <label>Categoría *</label>
                                                    <select
                                                        value={newCatalogForm.categoryId || ''}
                                                        onChange={e => {
                                                            const categoryId = e.target.value || null;
                                                            setNewCatalogForm(prev => ({
                                                                ...prev,
                                                                categoryId: categoryId,
                                                                subcategoryId: null // Resetear subcategoría
                                                            }));
                                                        }}
                                                        disabled={loadingCategories}
                                                    >
                                                        <option value="">Seleccionar categoría...</option>
                                                        {categoriesData.categories?.map(category => (
                                                            <option key={category.id} value={category.id}>
                                                                {category.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            {/* Selector de subcategoría */}
                                            <div className="catalog-wizard-form-group">
                                                <label>
                                                    {categoriesData.categories?.length === 1 ? 'Categoría *' : 'Subcategoría *'}
                                                </label>
                                                <select
                                                    value={newCatalogForm.subcategoryId || ''}
                                                    onChange={e => {
                                                        const subcategoryId = e.target.value || null;
                                                        let categoryId = newCatalogForm.categoryId;

                                                        // Si solo hay una categoría, auto-asignarla
                                                        if (categoriesData.categories?.length === 1) {
                                                            categoryId = categoriesData.categories[0]?.id || null;
                                                        }

                                                        setNewCatalogForm(prev => ({
                                                            ...prev,
                                                            categoryId: categoryId,
                                                            subcategoryId: subcategoryId
                                                        }));
                                                    }}
                                                    disabled={loadingCategories || (categoriesData.categories?.length > 1 && !newCatalogForm.categoryId)}
                                                >
                                                    <option value="">
                                                        {categoriesData.categories?.length === 1 ? 'Seleccionar categoría...' : 'Seleccionar subcategoría...'}
                                                    </option>
                                                    {categoriesData.categories?.length === 1 ? (
                                                        // Si solo hay una categoría, mostrar todas sus subcategorías
                                                        categoriesData.categories[0]?.subCategories?.map(subcategory => (
                                                            <option key={subcategory.id} value={subcategory.id}>
                                                                {subcategory.name}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        // Si hay múltiples categorías, mostrar solo las subcategorías de la categoría seleccionada
                                                        categoriesData.categories
                                                            ?.find(cat => cat.id === newCatalogForm.categoryId)
                                                            ?.subCategories?.map(subcategory => (
                                                                <option key={subcategory.id} value={subcategory.id}>
                                                                    {subcategory.name}
                                                                </option>
                                                            )) || []
                                                    )}
                                                </select>
                                            </div>
                                        </>
                                    )}

                                    {/* Selector multi-subcategoría para MULTI_SUBCATEGORY */}
                                    {newCatalogForm.type === 'MULTI_SUBCATEGORY' && (
                                        <div className="catalog-wizard-form-group" style={{ marginTop: '1rem' }}>
                                            <label>
                                                Categorías a incluir *
                                                {selectedSubcategoryIds.length > 0 && (
                                                    <span style={{ marginLeft: '0.5rem', color: '#fa7e17', fontWeight: '600' }}>
                                                        ({selectedSubcategoryIds.length} seleccionada{selectedSubcategoryIds.length !== 1 ? 's' : ''})
                                                    </span>
                                                )}
                                            </label>
                                            <div style={{
                                                maxHeight: '280px',
                                                overflowY: 'auto',
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                                backgroundColor: '#fff',
                                            }}>
                                                {loadingCategories ? (
                                                    <div style={{ padding: '1.5rem', textAlign: 'center' }}>
                                                        <ClipLoader color="#fa7e17" size={20} />
                                                    </div>
                                                ) : (
                                                    categoriesData.categories?.flatMap(cat => cat.subCategories || []).map(subcat => {
                                                        const checked = selectedSubcategoryIds.includes(subcat.id);
                                                        return (
                                                            <div
                                                                key={subcat.id}
                                                                onClick={() => setSelectedSubcategoryIds(prev =>
                                                                    prev.includes(subcat.id)
                                                                        ? prev.filter(id => id !== subcat.id)
                                                                        : [...prev, subcat.id]
                                                                )}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.75rem',
                                                                    padding: '0.7rem 0.75rem',
                                                                    cursor: 'pointer',
                                                                    borderBottom: '1px solid #f0f0f0',
                                                                    backgroundColor: checked ? '#fff8f0' : 'transparent',
                                                                    transition: 'background 0.15s ease',
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={() => {}}
                                                                    onClick={e => e.stopPropagation()}
                                                                    style={{ width: '18px', height: '18px', accentColor: '#fa7e17', flexShrink: 0, margin: 0, cursor: 'pointer' }}
                                                                />
                                                                <span style={{ fontSize: '0.9rem', color: '#333', fontWeight: checked ? '500' : '400', margin: 0 }}>
                                                                    {subcat.name}
                                                                </span>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Selector de productos para CUSTOM */}
                                    {newCatalogForm.type === 'CUSTOM' && (
                                        <div className="catalog-wizard-form-group" style={{ marginTop: '1rem' }}>
                                            <label>Productos del Catálogo *</label>
                                            <div style={{
                                                maxHeight: '350px',
                                                overflow: 'hidden',
                                                border: '1px solid #ddd',
                                                borderRadius: '8px',
                                                backgroundColor: '#fff'
                                            }}>
                                                {/* Buscador */}
                                                <div style={{
                                                    padding: '0.75rem',
                                                    borderBottom: '1px solid #eee',
                                                    backgroundColor: '#fafafa'
                                                }}>
                                                    <input
                                                        type="text"
                                                        placeholder="Buscar productos..."
                                                        value={productSearchQuery}
                                                        onChange={(e) => setProductSearchQuery(e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '0.6rem 0.75rem',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '6px',
                                                            fontSize: '0.9rem',
                                                            margin: 0
                                                        }}
                                                    />
                                                </div>

                                                {/* Contador de seleccionados */}
                                                <div style={{
                                                    padding: '0.6rem 0.75rem',
                                                    backgroundColor: '#fff',
                                                    borderBottom: '1px solid #eee',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '600',
                                                    color: '#fa7e17',
                                                    margin: 0
                                                }}>
                                                    {selectedProductIds.length} producto{selectedProductIds.length !== 1 ? 's' : ''} seleccionado{selectedProductIds.length !== 1 ? 's' : ''}
                                                </div>

                                                {/* Lista de productos con scroll */}
                                                {loadingAvailableProducts ? (
                                                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                                                        <ClipLoader color="#fa7e17" size={25} />
                                                        <p style={{ marginTop: '1rem', color: '#666', margin: '1rem 0 0 0' }}>Cargando productos...</p>
                                                    </div>
                                                ) : filteredAvailableProducts.length === 0 ? (
                                                    <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
                                                        <p style={{ margin: 0 }}>{productSearchQuery ? 'No se encontraron productos' : 'No tienes productos disponibles'}</p>
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        maxHeight: '240px',
                                                        overflowY: 'auto',
                                                        padding: 0,
                                                        margin: 0
                                                    }}>
                                                        {filteredAvailableProducts.map(product => (
                                                            <div
                                                                key={product.id}
                                                                onClick={() => toggleProductSelection(product.id)}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.75rem',
                                                                    padding: '0.7rem 0.75rem',
                                                                    margin: 0,
                                                                    cursor: 'pointer',
                                                                    borderBottom: '1px solid #f0f0f0',
                                                                    transition: 'background 0.2s ease',
                                                                    backgroundColor: isProductSelected(product.id) ? '#fff8f0' : 'transparent'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!isProductSelected(product.id)) {
                                                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!isProductSelected(product.id)) {
                                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                                    }
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isProductSelected(product.id)}
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleProductSelection(product.id);
                                                                    }}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    style={{
                                                                        width: '18px',
                                                                        height: '18px',
                                                                        cursor: 'pointer',
                                                                        accentColor: '#fa7e17',
                                                                        flexShrink: 0,
                                                                        margin: 0
                                                                    }}
                                                                />
                                                                <span style={{
                                                                    flex: 1,
                                                                    fontSize: '0.9rem',
                                                                    color: '#333',
                                                                    fontWeight: isProductSelected(product.id) ? '500' : '400',
                                                                    margin: 0,
                                                                    padding: 0
                                                                }}>
                                                                    {product.name}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Paso 2: Banner del catálogo */}
                                <div className={`catalog-wizard-step-content ${createModalStep === 2 ? 'wizard-step-content-active wizard-step-slide-in' : ''}`}>
                                    <div className="catalog-wizard-banner-upload-container">
                                        {!createBannerPreview ? (
                                            <div className="catalog-wizard-banner-upload-area">
                                                <FontAwesomeIcon icon={faImage} className="catalog-wizard-banner-upload-icon" />
                                                <h5 className="catalog-wizard-banner-upload-title">Selecciona la portada de tu catálogo</h5>
                                                <p className="catalog-wizard-banner-upload-subtitle">La imagen se comprimirá automáticamente al subirla</p>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleCreateBannerFileChange}
                                                    disabled={saving}
                                                    className="catalog-wizard-file-input-hidden"
                                                    id="wizard-banner-upload"
                                                />
                                                <label htmlFor="wizard-banner-upload" className="catalog-wizard-upload-button">
                                                    <FontAwesomeIcon icon={faPlus} />
                                                    Seleccionar Imagen
                                                </label>
                                            </div>
                                        ) : (
                                            <div className="catalog-wizard-banner-preview-container">
                                                <div className="catalog-wizard-banner-preview-image">
                                                    <img
                                                        src={createBannerPreview}
                                                        alt="Vista previa del banner"
                                                    />
                                                </div>
                                                <p className="catalog-wizard-banner-preview-success">
                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                    ¡Perfecto! Tu banner se ve genial
                                                </p>
                                                <button
                                                    type="button"
                                                    className="catalog-wizard-change-banner-btn"
                                                    onClick={() => {
                                                        setCreateBannerFile(null);
                                                        setCreateBannerPreview(null);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faPen} />
                                                    Cambiar Imagen
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>

                            {/* Footer con botones de navegación */}
                            <div className="catalog-wizard-footer">
                                {createModalStep === 1 ? (
                                    /* Paso 1: Información básica y productos */
                                    <>
                                        <button
                                            className="multiple-catalog-btn-secondary"
                                            onClick={closeCreateModal}
                                            disabled={saving}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            className="multiple-catalog-btn-primary"
                                            onClick={goToNextStep}
                                            disabled={saving || !newCatalogForm.internalName.trim() || !newCatalogForm.publicName.trim()}
                                        >
                                            Siguiente
                                            <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.5rem' }} />
                                        </button>
                                    </>
                                ) : (
                                    /* Paso 2: Banner */
                                    <>
                                        <button
                                            className="multiple-catalog-btn-secondary"
                                            onClick={goBackToBasicData}
                                            disabled={saving}
                                        >
                                            <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '0.5rem' }} />
                                            Atrás
                                        </button>
                                        <button
                                            className="multiple-catalog-btn-primary"
                                            onClick={handleCreateCatalog}
                                            disabled={saving || !createBannerFile}
                                        >
                                            {saving ? (
                                                <>
                                                    <ClipLoader size={16} color="white" />
                                                    <span style={{ marginLeft: '0.5rem' }}>Creando...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.5rem' }} />
                                                    Crear Catálogo
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal para editar catálogo */}
                {showEditModal && editingCatalog && (
                    <div className="multiple-catalog-modal-overlay" onClick={closeEditModal}>
                        <div className="multiple-catalog-modal-content" onClick={e => e.stopPropagation()}>
                            <div className="multiple-catalog-modal-header">
                                <h3>Editar Catálogo</h3>
                                <button onClick={closeEditModal} aria-label="Cerrar modal">
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <div className="multiple-catalog-modal-body">
                                <div className="multiple-catalog-form-group">
                                    <label>Nombre Interno (Identificación) *</label>
                                    <input
                                        type="text"
                                        value={editCatalogForm.internalName}
                                        onChange={e => setEditCatalogForm(prev => ({ ...prev, internalName: e.target.value }))}
                                        placeholder="Ej: CAT-VERANO-2024"
                                        maxLength={100}
                                    />
                                    <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                                        Nombre único para tu gestión interna
                                    </small>
                                </div>

                                <div className="multiple-catalog-form-group">
                                    <label>Nombre Público (Visible) *</label>
                                    <input
                                        type="text"
                                        value={editCatalogForm.publicName}
                                        onChange={e => setEditCatalogForm(prev => ({ ...prev, publicName: e.target.value }))}
                                        placeholder="Ej: Colección Verano"
                                        maxLength={100}
                                    />
                                    <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                                        Nombre que verán los comerciantes
                                    </small>
                                </div>

                                <div className="multiple-catalog-form-group">
                                    <label>Descripción</label>
                                    <textarea
                                        value={editCatalogForm.description}
                                        onChange={e => setEditCatalogForm(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Describe tu catálogo..."
                                        maxLength={200}
                                        rows={3}
                                    />
                                </div>

                                {hasMultipleNumbers && (
                                <div className="multiple-catalog-form-group">
                                    <label>Teléfono de WhatsApp</label>
                                    <input
                                        type="tel"
                                        value={editCatalogForm.telefono}
                                        onChange={e => setEditCatalogForm(prev => ({ ...prev, telefono: e.target.value }))}
                                        placeholder="Ej: +573001234567"
                                        maxLength={20}
                                    />
                                    <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                                        Si no se especifica, se usará el teléfono del proveedor
                                    </small>
                                </div>
                                )}

                                {hasMultiplePrices && (
                                <div className="multiple-catalog-form-group">
                                    <label>Precio a Mostrar</label>
                                    <select
                                        value={editCatalogForm.priceField || ''}
                                        onChange={e => setEditCatalogForm(prev => ({ ...prev, priceField: e.target.value || null }))}
                                    >
                                        <option value="">Todos los precios</option>
                                        <option value="price1">Precio 1</option>
                                        <option value="price2">Precio 2</option>
                                        <option value="price3">Precio 3</option>
                                    </select>
                                    <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
                                        Útil si tienes diferentes precios para diferentes vendedores o ciudades
                                    </small>
                                </div>
                                )}

                                <div className="multiple-catalog-type-info">
                                    <p><strong>Tipo:</strong> {getCatalogTypeDescription(editingCatalog)}</p>
                                    <small>El tipo de catálogo no se puede cambiar después de crearlo</small>
                                </div>
                            </div>

                            <div className="multiple-catalog-modal-footer">
                                <button
                                    className="multiple-catalog-btn-secondary"
                                    onClick={closeEditModal}
                                    disabled={saving}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="multiple-catalog-btn-primary"
                                    onClick={handleEditCatalog}
                                    disabled={saving || !editCatalogForm.internalName.trim() || !editCatalogForm.publicName.trim()}
                                >
                                    {saving ? (
                                        <>
                                            <ClipLoader size={16} color="white" />
                                            <span style={{ marginLeft: '0.5rem' }}>Guardando...</span>
                                        </>
                                    ) : (
                                        'Guardar Cambios'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal para subir banner */}
                {showBannerModal && bannerCatalog && (
                    <div className="multiple-catalog-modal-overlay" onClick={closeBannerModal}>
                        <div className="multiple-catalog-modal-content" onClick={e => e.stopPropagation()}>
                            <div className="multiple-catalog-modal-header">
                                <h3>Cambiar Banner - {bannerCatalog.publicName || bannerCatalog.name}</h3>
                                <button onClick={closeBannerModal} aria-label="Cerrar modal">
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <div className="multiple-catalog-modal-body">
                                <div className="multiple-catalog-form-group">
                                    <label>Seleccionar imagen del banner</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleBannerFileChange}
                                        disabled={uploadingBanner}
                                    />
                                    <small>Tamaño máximo: 300KB. Cualquier formato de imagen.</small>
                                </div>

                                {bannerPreview && (
                                    <div className="multiple-catalog-form-group">
                                        <label>Vista previa</label>
                                        <div className="multiple-catalog-banner-preview">
                                            <img
                                                src={bannerPreview}
                                                alt="Vista previa del banner"
                                                style={{
                                                    width: '100%',
                                                    height: '150px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    border: '2px solid #e9ecef'
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {bannerCatalog.bannerUrl && (
                                    <div className="multiple-catalog-form-group">
                                        <label>Banner actual</label>
                                        <div className="multiple-catalog-current-banner">
                                            <img
                                                src={bannerCatalog.bannerUrl}
                                                alt="Banner actual"
                                                style={{
                                                    width: '100%',
                                                    height: '150px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    border: '2px solid #e9ecef'
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="multiple-catalog-modal-footer">
                                <button
                                    className="multiple-catalog-btn-secondary"
                                    onClick={closeBannerModal}
                                    disabled={uploadingBanner}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="multiple-catalog-btn-primary"
                                    onClick={handleBannerUpload}
                                    disabled={uploadingBanner || !bannerFile}
                                >
                                    {uploadingBanner ? (
                                        <>
                                            <ClipLoader size={16} color="white" />
                                            <span style={{ marginLeft: '0.5rem' }}>Subiendo...</span>
                                        </>
                                    ) : (
                                        'Subir Banner'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
            {!isEmbedded() && <NavInf />}
            {!isEmbedded() && <Footer navinf={true} />}
            {alert && (
                <Alert
                    type={alert.type}
                    message={alert.message}
                    onClose={() => setAlert(null)}
                />
            )}

            {/* Modal de diseño PDF */}
            <PDFDesignModal
                isOpen={showPDFDesignModal}
                onClose={closePDFDesignModal}
                catalog={pdfDesignCatalog}
                providerInfo={user?.proveedorInfo}
            />
        </>
    );
};

export default MultipleCatalogManager;